import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MoveHorizontal, ArrowUpRight, Compass } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";

/* ============================================================
 * "Find your fit" homepage gallery.
 * Lifestyle-first entry into /relocating. Same horizontal
 * drag/wheel/swipe mechanic as the previous Neighbourhoods
 * gallery — different content. Four vibe cards + one Scout
 * invitation. Each card deep-links to /relocating with the
 * matching ?filter= chip pre-selected.
 *
 * IMAGES: temporarily using close-fitting stock while the user
 * uploads the five branded photos (fit-families.jpg,
 * fit-downtown.jpg, fit-nature.jpg, fit-students.jpg,
 * fit-scout.jpg). Swap the `image` URLs below when they land.
 * ============================================================ */
const FITS = [
  {
    slug: "families",
    kicker: "For families",
    title: "Room to grow",
    line:
      "Quiet streets, schools you can walk to, parks at the corner.",
    href: "/relocating?filter=families",
    image:
      "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    slug: "working-downtown",
    kicker: "For downtown workers",
    title: "Close to the action",
    line: "Short commutes, long evenings.",
    href: "/relocating?filter=working-downtown",
    image:
      "https://images.unsplash.com/photo-1519501025264-65ba15a82390?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    slug: "near-nature",
    kicker: "For the outdoorsy",
    title: "Trail out the back door",
    line:
      "River valleys, bike paths, weekend escapes that start at home.",
    href: "/relocating?filter=near-nature",
    image:
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    slug: "students",
    kicker: "For students & first places",
    title: "Campus adjacent",
    line: "Walkable, lively, affordable.",
    href: "/relocating?filter=students",
    image:
      "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  // 5th card — Scout invitation, pine background instead of a photo.
  {
    slug: "scout",
    kicker: "New here entirely?",
    title: "We'll match you before you land.",
    line:
      "Our Scouts pair you with a neighbourhood and a home before your flight — free.",
    href: "/relocating",
    scout: true,
  },
];

export const NeighbourhoodsGallery = () => {
  const scroller = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: 0 });
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  // Wheel -> horizontal, releasing at edges so the page keeps scrolling.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta =
        Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      const atStart = el.scrollLeft <= 0 && delta < 0;
      const atEnd =
        el.scrollLeft >= el.scrollWidth - el.clientWidth - 1 && delta > 0;
      if (atStart || atEnd) return;
      el.scrollLeft += delta;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e) => {
    drag.current = {
      down: true,
      startX: e.clientX,
      startScroll: scroller.current.scrollLeft,
      moved: 0,
    };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (hover) setCursor({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
    if (!drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
    scroller.current.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = () => {
    drag.current.down = false;
    setDragging(false);
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: "#EDE9E0" }}
      data-nav-theme="light"
      data-testid="home-fit"
    >
      <div className="mx-auto max-w-[1440px] px-6 pt-24 md:px-10 md:pt-36">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-[720px]">
            <Reveal>
              <Eyebrow testId="fit-eyebrow">Find your fit</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                className="font-display mt-7 max-w-[18ch]"
                style={{
                  fontSize: "clamp(36px,5vw,72px)",
                  color: "#201E1A",
                  lineHeight: 1.02,
                }}
                data-testid="fit-title"
              >
                Tell us how you live. We&rsquo;ll find{" "}
                <span className="italic" style={{ color: "#C89A4E" }}>
                  where.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p
                className="mt-5 text-[15px] md:text-[16px]"
                style={{ color: "rgba(32,30,26,0.72)", maxWidth: "58ch" }}
                data-testid="fit-subcopy"
              >
                Calgary, Edmonton, and the neighbourhoods in between —
                matched to your life, not just your budget.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <span
              className="label-caps hidden items-center gap-2 text-[11px] md:inline-flex"
              style={{ color: "rgba(32,30,26,0.5)" }}
            >
              <MoveHorizontal size={16} /> Drag, swipe or scroll
            </span>
          </Reveal>
        </div>
      </div>

      {/* gallery */}
      <div
        className="relative mt-14"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => {
          setHover(false);
          endDrag();
        }}
      >
        <div
          ref={scroller}
          className="no-scrollbar flex gap-6 overflow-x-auto overflow-y-hidden px-6 pb-16 md:px-10"
          style={{ cursor: isMobile ? "auto" : dragging ? "grabbing" : "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          data-testid="fit-scroller"
        >
          {FITS.map((f) => (
            <FitCard
              key={f.slug}
              fit={f}
              onClickGuard={(e) => {
                if (drag.current.moved > 6) e.preventDefault();
              }}
            />
          ))}
          <div className="shrink-0" style={{ width: 1 }} aria-hidden="true" />
        </div>

        {/* custom DRAG cursor hint (desktop) */}
        <AnimatePresence>
          {hover && !isMobile && (
            <motion.div
              className="pointer-events-none absolute z-20 flex items-center justify-center rounded-full"
              style={{
                left: cursor.x,
                top: cursor.y,
                translateX: "-50%",
                translateY: "-50%",
                width: 84,
                height: 84,
                backgroundColor: "rgba(36,56,46,0.92)",
                backdropFilter: "blur(4px)",
              }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: dragging ? 0.85 : 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <span
                className="label-caps flex items-center gap-1 text-[11px]"
                style={{ color: "#E3B268" }}
              >
                <MoveHorizontal size={14} /> Drag
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

/* ---- single card ---- */
const FitCard = ({ fit, onClickGuard }) => (
  <Link
    to={fit.href}
    onClick={onClickGuard}
    data-testid={`fit-card-${fit.slug}`}
    className="group relative shrink-0"
    style={{ width: "clamp(268px, 30vw, 360px)" }}
    draggable={false}
  >
    <div
      className="relative overflow-hidden"
      style={{
        borderRadius: 24,
        height: "clamp(420px, 56vh, 540px)",
        backgroundColor: fit.scout ? "#24382E" : "#201E1A",
      }}
    >
      {fit.scout ? (
        <ScoutContent fit={fit} />
      ) : (
        <PhotoContent fit={fit} />
      )}
    </div>
  </Link>
);

const PhotoContent = ({ fit }) => (
  <>
    <img
      src={fit.image}
      alt={fit.title}
      draggable={false}
      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
    />
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(to top, rgba(20,35,27,0.86) 0%, rgba(20,35,27,0.18) 46%, rgba(20,35,27,0) 70%)",
      }}
    />
    <div className="absolute inset-x-0 bottom-0 p-6">
      <p
        className="label-caps text-[10px]"
        style={{ color: "rgba(227,178,104,0.9)" }}
      >
        {fit.kicker}
      </p>
      <h3
        className="font-display mt-2"
        style={{
          color: "#F8F5EF",
          fontSize: "clamp(28px,2.8vw,36px)",
          lineHeight: 1.02,
          letterSpacing: "-0.01em",
        }}
      >
        {fit.title}
      </h3>
      <p
        className="mt-2 text-[14px]"
        style={{ color: "rgba(237,233,224,0.82)" }}
      >
        {fit.line}
      </p>
      <span
        className="mt-4 inline-flex items-center gap-1.5 text-[12px] transition-transform duration-500 group-hover:translate-x-1"
        style={{ color: "#E3B268" }}
      >
        <span className="label-caps text-[10px]">Explore</span>
        <ArrowUpRight size={14} />
      </span>
    </div>
  </>
);

const ScoutContent = ({ fit }) => (
  <>
    {/* Local keyframes — kept scoped to this card so they don't leak into
        the global stylesheet. `prefers-reduced-motion` opts out cleanly. */}
    <style>{`
      @keyframes scout-compass-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @keyframes scout-aurora-drift {
        0%   { transform: translate3d(-8%, -4%, 0) rotate(-14deg) scale(1.05); opacity: 0.55; }
        50%  { transform: translate3d(6%,  4%, 0) rotate(-10deg) scale(1.10); opacity: 0.75; }
        100% { transform: translate3d(-8%, -4%, 0) rotate(-14deg) scale(1.05); opacity: 0.55; }
      }
      .scout-compass { animation: none; }
      .group:hover .scout-compass {
        animation: scout-compass-spin 24s linear infinite;
      }
      .scout-aurora { animation: scout-aurora-drift 14s ease-in-out infinite; }
      .scout-compass-wrap { opacity: 0.08; transition: opacity 800ms ease; }
      .group:hover .scout-compass-wrap { opacity: 0.28; }
      @media (prefers-reduced-motion: reduce) {
        .group:hover .scout-compass { animation: none; }
        .scout-aurora { animation: none; }
      }
    `}</style>

    {/* Base warm radial glow so the scout card doesn't read as flat pine */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "radial-gradient(120% 80% at 12% 10%, rgba(200,154,78,0.22) 0%, rgba(36,56,46,0) 60%)," +
          "radial-gradient(80% 60% at 100% 100%, rgba(174,196,199,0.14) 0%, rgba(36,56,46,0) 55%)",
      }}
    />

    {/* Aurora ribbon — softly drifting arc of glacier + gold. Ambient at
        rest (~40% visible), brightens on hover. Heavy blur keeps it read
        as light, not a shape. */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ opacity: 0.4, transition: "opacity 700ms ease" }}
    >
      <div
        className="scout-aurora absolute"
        style={{
          top: "-25%",
          left: "-15%",
          right: "-15%",
          height: "80%",
          filter: "blur(46px)",
          background:
            "linear-gradient(100deg, rgba(174,196,199,0) 0%, rgba(174,196,199,0.35) 22%, rgba(227,178,104,0.35) 52%, rgba(200,154,78,0.28) 72%, rgba(174,196,199,0) 100%)",
          borderRadius: "50%",
          transform: "rotate(-12deg)",
        }}
      />
    </div>
    {/* Hover intensifier for the aurora — bumps the wrapper opacity so
        the drifting ribbon reads more clearly when the card is engaged. */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
      style={{
        background:
          "radial-gradient(70% 50% at 78% 30%, rgba(227,178,104,0.16) 0%, rgba(36,56,46,0) 70%)," +
          "radial-gradient(60% 60% at 20% 80%, rgba(174,196,199,0.12) 0%, rgba(36,56,46,0) 70%)",
      }}
    />

    {/* Compass rose — subtle watermark right-of-centre. Fades in and
        rotates slowly on hover; sits behind the copy. */}
    <div
      aria-hidden
      className="scout-compass-wrap pointer-events-none absolute"
      style={{
        top: "36%",
        right: "-12%",
        width: 260,
        height: 260,
      }}
    >
      <CompassRose />
    </div>

    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-multiply grain"
    />

    {/* Copy — sits above all decorative layers */}
    <div className="relative z-10 flex h-full flex-col justify-between p-7">
      <div>
        <span
          className="label-caps inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[9px]"
          style={{
            backgroundColor: "#E3B268",
            color: "#14231B",
            letterSpacing: "0.24em",
          }}
          data-testid="fit-scout-chip"
        >
          <Compass size={11} strokeWidth={1.8} />
          ROAM Scout&trade;
        </span>
      </div>
      <div>
        <p
          className="label-caps text-[10px]"
          style={{ color: "rgba(227,178,104,0.9)" }}
        >
          {fit.kicker}
        </p>
        <h3
          className="font-display mt-2 max-w-[14ch]"
          style={{
            color: "#F8F5EF",
            fontSize: "clamp(28px,2.9vw,36px)",
            lineHeight: 1.02,
            letterSpacing: "-0.01em",
          }}
        >
          {fit.title.split(" ").map((w, i, arr) =>
            i === arr.length - 1 ? (
              <span key={i} className="italic" style={{ color: "#E3B268" }}>
                {w}
              </span>
            ) : (
              <span key={i}>{w} </span>
            ),
          )}
        </h3>
        <p
          className="mt-3 text-[14px] leading-[1.55]"
          style={{ color: "rgba(237,233,224,0.78)" }}
        >
          {fit.line}
        </p>
        <span
          className="mt-6 inline-flex items-center gap-1.5 text-[12px] transition-transform duration-500 group-hover:translate-x-1"
          style={{ color: "#E3B268" }}
        >
          <span className="label-caps text-[10px]">Meet a scout</span>
          <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  </>
);

/* Thin-line compass rose. Larch strokes on transparent — the parent
 * controls opacity + rotation so this can be reused elsewhere. */
const CompassRose = () => (
  <svg
    viewBox="0 0 200 200"
    className="scout-compass"
    style={{
      width: "100%",
      height: "100%",
      transformOrigin: "50% 50%",
      willChange: "transform",
    }}
    fill="none"
    stroke="#C89A4E"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* outer + inner rings */}
    <circle cx="100" cy="100" r="90" strokeOpacity="0.6" />
    <circle cx="100" cy="100" r="72" strokeOpacity="0.35" />
    <circle cx="100" cy="100" r="42" strokeOpacity="0.5" />
    <circle cx="100" cy="100" r="4" fill="#C89A4E" stroke="none" />

    {/* cardinal points (N/E/S/W) — long diamond spokes */}
    <path d="M100 12 L106 96 L100 100 L94 96 Z" fill="#C89A4E" fillOpacity="0.35" />
    <path d="M100 188 L94 104 L100 100 L106 104 Z" fill="#C89A4E" fillOpacity="0.25" />
    <path d="M188 100 L104 106 L100 100 L104 94 Z" fill="#C89A4E" fillOpacity="0.25" />
    <path d="M12 100 L96 94 L100 100 L96 106 Z" fill="#C89A4E" fillOpacity="0.25" />

    {/* intercardinal spokes (NE / SE / SW / NW) — thin */}
    <line x1="100" y1="100" x2="162" y2="38" strokeOpacity="0.4" />
    <line x1="100" y1="100" x2="162" y2="162" strokeOpacity="0.4" />
    <line x1="100" y1="100" x2="38" y2="162" strokeOpacity="0.4" />
    <line x1="100" y1="100" x2="38" y2="38" strokeOpacity="0.4" />

    {/* tick marks around the outer ring */}
    {Array.from({ length: 24 }).map((_, i) => {
      const a = (i * Math.PI * 2) / 24;
      const x1 = 100 + Math.cos(a) * 90;
      const y1 = 100 + Math.sin(a) * 90;
      const x2 = 100 + Math.cos(a) * (i % 3 === 0 ? 78 : 84);
      const y2 = 100 + Math.sin(a) * (i % 3 === 0 ? 78 : 84);
      return (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          strokeOpacity={i % 3 === 0 ? 0.6 : 0.3}
        />
      );
    })}

    {/* cardinal letters */}
    <g
      fontFamily="ui-serif, Georgia, serif"
      fontSize="12"
      fill="#E3B268"
      textAnchor="middle"
    >
      <text x="100" y="30">N</text>
      <text x="176" y="104">E</text>
      <text x="100" y="182">S</text>
      <text x="24" y="104">W</text>
    </g>
  </svg>
);

export default NeighbourhoodsGallery;
